import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import type { AnnouncementChannel, AnnouncementCreate, AnnouncementUpdate, AnnouncementView } from "./model";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

/** Value that settles `delay` ms after the last change (search box, reach estimate). */
export function useDebounced<T>(value: T, delay = DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return settled;
}

/** `GET /v1/announcements?status=&q=&cursor=` (cursor pages, "Load more"). */
export function useAnnouncementList(view: AnnouncementView, q: string) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: [...engageKeys.announcements(wsId), "list", view, q],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/announcements", { params: { query: { status: view, q: q || null, cursor: pageParam, limit: PAGE_SIZE } } })),
    getNextPageParam: (page) => page.next_cursor,
  });
}

export function useAnnouncementStats() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: [...engageKeys.announcements(wsId), "stats"], queryFn: () => unwrap(api.GET("/v1/announcements/stats")) });
}

export interface ReachInput {
  audiences: number[];
  segments: string[];
  channels: AnnouncementChannel[];
}

/** `POST /v1/announcements/estimate-reach`, debounced while the composer changes. */
export function useReachEstimate(input: ReachInput, enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const settled = useDebounced(input);
  return useQuery({
    queryKey: [...engageKeys.announcements(wsId), "reach", settled],
    queryFn: () => unwrap(api.POST("/v1/announcements/estimate-reach", { body: settled })),
    enabled: enabled && settled.audiences.length > 0,
    staleTime: 60_000,
  });
}

type RowAction = "send" | "duplicate" | "archive" | "unarchive" | "delete";

/** Card actions; every success refreshes lists + stats. */
export function useAnnouncementAction() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: RowAction }) => {
      const path = { params: { path: { announcement_id: id } } };
      if (action === "delete") return unwrap(api.DELETE("/v1/announcements/{announcement_id}", path));
      if (action === "send") return unwrap(api.POST("/v1/announcements/{announcement_id}/send", path));
      if (action === "duplicate") return unwrap(api.POST("/v1/announcements/{announcement_id}/duplicate", path));
      if (action === "archive") return unwrap(api.POST("/v1/announcements/{announcement_id}/archive", path));
      return unwrap(api.POST("/v1/announcements/{announcement_id}/unarchive", path));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: engageKeys.announcements(wsId) }),
  });
}

export type SaveRequest =
  | { kind: "create"; body: AnnouncementCreate }
  | { kind: "update"; id: string; body: AnnouncementUpdate; sendNow: boolean };

/** Composer save: create, or patch (+ send now when an edited draft goes out immediately). */
export function useSaveAnnouncement() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: SaveRequest) => {
      if (request.kind === "create") return unwrap(api.POST("/v1/announcements", { body: request.body }));
      const path = { params: { path: { announcement_id: request.id } } };
      const updated = await unwrap(api.PATCH("/v1/announcements/{announcement_id}", { ...path, body: request.body }));
      return request.sendNow ? unwrap(api.POST("/v1/announcements/{announcement_id}/send", path)) : updated;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: engageKeys.announcements(wsId) }),
  });
}
